import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'urql';

import { PAY_V2_QUERY, PositionsEntityKind } from '@revelio/data-access';

import { useCustomSkillSelectionList } from '../../../shared/selection-lists/hooks/useCustomSkillSelectionList';
import type { UpliftMode } from '../IndividualPotentialPage';
import type { ResolvedProfileIds } from './useResolveProfileIds';
import type { RoleSkillTimepoint } from './useRoleSkillComposition';
import {
  buildDistributionFromHistogram,
  BUCKET_SIZE as DISTRIBUTION_BUCKET_SIZE,
  findPercentileValue,
} from './useSalaryDistribution';
import type { MissingSkill, SkillTrend } from './useSkillGap';

export interface SkillUpliftResult {
  skillId: number;
  skillName: string;
  roleShare: number;
  targetSalaryWithSkill: number | null;
  upliftPercent: number | null;
  upliftDollar: number | null;
  trend: SkillTrend;
  trendChangePercent: number;
  timeseries: ReadonlyArray<RoleSkillTimepoint>;
}

export interface UseSkillPayUpliftResult {
  skillUplifts: SkillUpliftResult[];
  isLoading: boolean;
  progress: number;
}

type SkillLevelKey = 'skill_0' | 'skill_1' | 'skill_2' | 'skill_3' | 'skill_4';

const LEVEL_KEYS: SkillLevelKey[] = [
  'skill_0',
  'skill_1',
  'skill_2',
  'skill_3',
  'skill_4',
];

/**
 * Look up a skill's level_id by searching across taxonomy levels.
 * Returns the level_id (1-5) where this skill ID was found.
 */
const findSkillLevelId = (
  skillId: number,
  taxonomyData: Record<
    SkillLevelKey,
    { refs: Array<{ id: number }> } | undefined
  >
): number => {
  for (let i = 0; i < LEVEL_KEYS.length; i++) {
    const levelKey = LEVEL_KEYS[i];
    if (!levelKey) continue;
    const level = taxonomyData[levelKey];
    if (level?.refs?.some((ref) => ref.id === skillId)) {
      return i + 1; // level_id is 1-indexed
    }
  }
  // Default to level 1 (skill_k150) if not found
  return 1;
};

const BUCKET_SIZE = 1000;

/**
 * Compute the median from a histogram (array of counts per salary bucket).
 * Each bucket i represents salary = i * BUCKET_SIZE.
 */
const medianFromHistogram = (
  histogramData: ReadonlyArray<number | null>
): number | null => {
  const counts = histogramData.map((c) => c ?? 0);
  const totalCount = counts.reduce((sum, c) => sum + c, 0);
  if (totalCount === 0) return null;

  const halfCount = totalCount / 2;
  let cumulative = 0;
  for (let i = 0; i < counts.length; i++) {
    cumulative += counts[i] ?? 0;
    if (cumulative >= halfCount) {
      return i * BUCKET_SIZE;
    }
  }
  return null;
};

export const useSkillPayUplift = (
  missingSkills: MissingSkill[],
  resolvedIds: ResolvedProfileIds,
  userSalary: number,
  upliftMode: UpliftMode = 'median',
  userPercentile: number | null = null
): UseSkillPayUpliftResult => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [results, setResults] = useState<Map<number, number | null>>(new Map());
  const prevKeyRef = useRef<string>('');

  const [{ data: taxonomyData }] = useCustomSkillSelectionList(0);

  // Reset when missing skills or uplift mode change
  const skillsKey = missingSkills.map((s) => s.id).join(',');
  const compositeKey = `${skillsKey}|${upliftMode}|${userPercentile}`;
  useEffect(() => {
    if (compositeKey !== prevKeyRef.current) {
      prevKeyRef.current = compositeKey;
      setCurrentIndex(0);
      setResults(new Map());
    }
  }, [compositeKey]);

  const currentSkill =
    currentIndex < missingSkills.length
      ? missingSkills[currentIndex]
      : undefined;

  const taxonomy = useMemo(() => {
    if (!taxonomyData?.selectionList) return undefined;
    const sl = taxonomyData.selectionList;
    return {
      skill_0: sl.skill_0 as { refs: Array<{ id: number }> } | undefined,
      skill_1: sl.skill_1 as { refs: Array<{ id: number }> } | undefined,
      skill_2: sl.skill_2 as { refs: Array<{ id: number }> } | undefined,
      skill_3: sl.skill_3 as { refs: Array<{ id: number }> } | undefined,
      skill_4: sl.skill_4 as { refs: Array<{ id: number }> } | undefined,
    };
  }, [taxonomyData]);

  const payVariables = useMemo(() => {
    if (
      !currentSkill ||
      !resolvedIds.roleId ||
      !taxonomy ||
      currentIndex >= missingSkills.length
    ) {
      return undefined;
    }

    const levelId = findSkillLevelId(currentSkill.id, taxonomy);

    return {
      req: {
        entities: [
          {
            EntID: resolvedIds.roleId,
            EntType: PositionsEntityKind.RoleK150,
          },
        ],
        filters: {
          raw: false,
          ...(resolvedIds.seniorityId
            ? { seniority: [resolvedIds.seniorityId] }
            : {}),
          custom_skill: {
            taxonomy_id: 0,
            from_new_refs: true,
            entities: [
              {
                entity_id: currentSkill.id,
                level_id: levelId,
              },
            ],
          },
        },
      },
    };
  }, [
    currentSkill,
    resolvedIds.roleId,
    resolvedIds.seniorityId,
    taxonomy,
    currentIndex,
    missingSkills.length,
  ]);

  const [{ data, fetching, error }] = useQuery({
    query: PAY_V2_QUERY,
    variables: payVariables ?? {
      req: { entities: [], filters: { raw: false } },
    },
    pause: !payVariables,
  });

  // When data arrives, record result and advance
  useEffect(() => {
    if (!currentSkill || fetching || currentIndex >= missingSkills.length) {
      return;
    }

    if (data || error) {
      let targetSalary: number | null = null;

      if (data && !error) {
        const histogram = data.pay_v2?.entities?.[0]?.metrics?.histogram;
        const totalHistogram = histogram?.total;
        if (totalHistogram && totalHistogram.length > 0) {
          if (upliftMode === 'percentile' && userPercentile !== null) {
            const distribution = buildDistributionFromHistogram(
              totalHistogram.map((c: number | null) => c ?? 0),
              DISTRIBUTION_BUCKET_SIZE
            );
            targetSalary = findPercentileValue(
              distribution,
              userPercentile / 100
            );
          } else {
            targetSalary = medianFromHistogram(totalHistogram);
          }
        }
      }

      setResults((prev) => {
        const next = new Map(prev);
        next.set(currentSkill.id, targetSalary);
        return next;
      });
      setCurrentIndex((prev) => prev + 1);
    }
  }, [
    data,
    error,
    fetching,
    currentSkill,
    currentIndex,
    missingSkills.length,
    upliftMode,
    userPercentile,
  ]);

  const skillUplifts = useMemo((): SkillUpliftResult[] => {
    return missingSkills.map((skill) => {
      const targetSalaryWithSkill = results.get(skill.id) ?? null;

      let upliftPercent: number | null = null;
      let upliftDollar: number | null = null;

      if (targetSalaryWithSkill !== null && userSalary > 0) {
        upliftDollar = targetSalaryWithSkill - userSalary;
        upliftPercent =
          ((targetSalaryWithSkill - userSalary) / userSalary) * 100;
      }

      return {
        skillId: skill.id,
        skillName: skill.longName || skill.shortName,
        roleShare: skill.roleShare,
        targetSalaryWithSkill,
        upliftPercent,
        upliftDollar,
        trend: skill.trend,
        trendChangePercent: skill.trendChangePercent,
        timeseries: skill.timeseries,
      };
    });
  }, [missingSkills, results, userSalary]);

  const isLoading =
    missingSkills.length > 0 && currentIndex < missingSkills.length;

  const progress =
    missingSkills.length > 0 ? currentIndex / missingSkills.length : 1;

  return {
    skillUplifts,
    isLoading,
    progress,
  };
};
